// IP Address validation service for AndonX access control
import { config } from '../config';

// Get approved IPs and ranges from config
const APPROVED_IPS = config.ipAccess?.approvedIPs || [
  '127.0.0.1',        // localhost
  '::1',              // localhost IPv6
];

const APPROVED_RANGES = config.ipAccess?.approvedRanges || [
  '192.168.1.0/24',   // Example: Strand Products internal network
  '10.0.0.0/8',       // Example: Corporate network
  '172.16.0.0/12',    // Example: Additional corporate range
];

/**
 * Convert IP address to numeric value for comparison
 * @param {string} ip - IP address string
 * @returns {number} - Numeric representation of IP
 */
function ipToNumber(ip) {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet), 0) >>> 0;
}

/**
 * Check if an IP address is within a CIDR range
 * @param {string} ip - IP address to check
 * @param {string} cidr - CIDR range (e.g., '192.168.1.0/24')
 * @returns {boolean} - True if IP is in range
 */
function isIpInRange(ip, cidr) {
  const [range, bits = '32'] = cidr.split('/');
  const mask = ~((1 << (32 - parseInt(bits))) - 1);
  const ipNum = ipToNumber(ip);
  const rangeNum = ipToNumber(range);
  return (ipNum & mask) === (rangeNum & mask);
}

/**
 * Get client IP address from various sources
 * @returns {Promise<string>} - Client IP address
 */
export async function getClientIP() {
  try {
    // Method 1: Use a public IP service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Failed to get IP from ipify.org:', error);

    try {
      // Method 2: Alternative IP service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data.ip;
    } catch (error2) {
      console.warn('Failed to get IP from ipapi.co:', error2);

      // Method 3: Use a simple service
      try {
        const response = await fetch('https://httpbin.org/ip');
        const data = await response.json();
        return data.origin;
      } catch (error3) {
        console.error('All IP detection methods failed:', error3);
        return null;
      }
    }
  }
}

/**
 * Check if IP access control is enabled
 * @returns {boolean} - True if IP access control is enabled
 */
export function isIPAccessEnabled() {
  return config.ipAccess?.enabled !== false;
}

/**
 * Check if the current IP address is approved for access
 * @returns {Promise<{approved: boolean, ip: string, reason: string}>}
 */
export async function checkIPAccess() {
  try {
    const clientIP = await getClientIP();

    if (!clientIP) {
      return {
        approved: false,
        ip: 'Unknown',
        reason: 'Unable to determine IP address'
      };
    }

    // Check exact IP matches
    if (APPROVED_IPS.includes(clientIP)) {
      return {
        approved: true,
        ip: clientIP,
        reason: 'Exact IP match'
      };
    }

    // Check IP ranges
    for (const range of APPROVED_RANGES) {
      if (isIpInRange(clientIP, range)) {
        return {
          approved: true,
          ip: clientIP,
          reason: `IP in approved range: ${range}`
        };
      }
    }

    // If we get here, IP is not approved
    return {
      approved: false,
      ip: clientIP,
      reason: 'IP not in approved list'
    };
  } catch (error) {
    console.error('Error checking IP access:', error);
    return {
      approved: false,
      ip: 'Error',
      reason: 'Error checking IP address'
    };
  }
}

/**
 * Add an IP address or range to the approved list
 * @param {string} ipOrRange - IP address or CIDR range
 */
export function addApprovedIP(ipOrRange) {
  if (ipOrRange.includes('/')) {
    // It's a CIDR range
    if (!APPROVED_RANGES.includes(ipOrRange)) {
      APPROVED_RANGES.push(ipOrRange);
    }
  } else {
    // It's a single IP
    if (!APPROVED_IPS.includes(ipOrRange)) {
      APPROVED_IPS.push(ipOrRange);
    }
  }
}

/**
 * Remove an IP address or range from the approved list
 * @param {string} ipOrRange - IP address or CIDR range to remove
 */
export function removeApprovedIP(ipOrRange) {
  if (ipOrRange.includes('/')) {
    const index = APPROVED_RANGES.indexOf(ipOrRange);
    if (index > -1) {
      APPROVED_RANGES.splice(index, 1);
    }
  } else {
    const index = APPROVED_IPS.indexOf(ipOrRange);
    if (index > -1) {
      APPROVED_IPS.splice(index, 1);
    }
  }
}

/**
 * Get the current list of approved IPs and ranges
 * @returns {object} - Object containing approved IPs and ranges
 */
export function getApprovedIPs() {
  return {
    ips: [...APPROVED_IPS],
    ranges: [...APPROVED_RANGES]
  };
}
